import { useState, useMemo, useEffect, useCallback } from 'react';
import { X, CreditCard, Truck, CheckCircle, ArrowLeft, Lock, Store, Package, ChevronUp, ChevronDown, Tag, Loader2, AlertCircle } from 'lucide-react';
import { useMarketplace } from '../../context/MarketplaceContext';
import { getMerchantById, getProductById, COMMISSION_RATES, PRODUCTS } from '../../data/marketplaceData';
import ProductCard from './ProductCard';
import { request, getAuthToken } from '../../services/bonndleApi';

/**
 * Cashback Code Generation Flow:
 *
 * 1. Flutter WebView injects deal info into localStorage:
 *    - bonndle:group_id
 *    - bonndle:deal_id
 *    - bonndle:merchant_slug
 *    - bonndle:cashback_percent
 *    - bonndle:min_purchase_amount
 *
 * 2. When user proceeds from shipping to payment:
 *    - Generate cashback code via POST /group-code/{groupId}/deal/{dealId}
 *    - Auto-apply the discount to the order total
 *
 * 3. Display the generated code and cashback percentage
 */

export default function CheckoutDrawer() {
  const {
    showCheckout,
    setShowCheckout,
    setShowCart,
    getCartTotals,
    checkout,
    cart,
  } = useMarketplace();

  const [step, setStep] = useState('shipping'); // shipping, payment, confirmation
  const [isProcessing, setIsProcessing] = useState(false);
  const [completedOrder, setCompletedOrder] = useState(null);
  const [orderSummaryExpanded, setOrderSummaryExpanded] = useState(true);

  // Cashback code state
  const [dealInfo, setDealInfo] = useState(null);
  const [cashbackCode, setCashbackCode] = useState(null);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [codeError, setCodeError] = useState(null);
  const [codeCopied, setCodeCopied] = useState(false);

  // Form states
  const [shippingForm, setShippingForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
  });

  const [paymentForm, setPaymentForm] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardName: '',
  });

  const totals = getCartTotals();

  // Read deal info from localStorage (injected by Flutter WebView)
  useEffect(() => {
    const groupId = localStorage.getItem('bonndle:group_id');
    const dealId = localStorage.getItem('bonndle:deal_id');
    const merchantSlug = localStorage.getItem('bonndle:merchant_slug');
    const cashbackPercent = localStorage.getItem('bonndle:cashback_percent');
    const minPurchaseAmount = localStorage.getItem('bonndle:min_purchase_amount');

    if (groupId && dealId) {
      setDealInfo({
        groupId,
        dealId,
        merchantSlug,
        cashbackPercent: cashbackPercent ? parseFloat(cashbackPercent) : null,
        minPurchaseAmount: minPurchaseAmount ? parseFloat(minPurchaseAmount) : null,
      });
    }
  }, [showCheckout]);

  // Generate cashback code via API
  const generateCashbackCode = useCallback(async () => {
    if (!dealInfo?.groupId || !dealInfo?.dealId) return;
    if (!getAuthToken()) {
      setCodeError('Please log in to generate your cashback code');
      return;
    }

    setIsGeneratingCode(true);
    setCodeError(null);

    try {
      const response = await request(`/group-code/${dealInfo.groupId}/deal/${dealInfo.dealId}`, {
        method: 'POST',
      });

      if (response?.code) {
        setCashbackCode(response.code);
      } else {
        throw new Error('No code received from server');
      }
    } catch (error) {
      console.error('Failed to generate cashback code:', error);
      setCodeError(error.message || 'Failed to generate cashback code');
    } finally {
      setIsGeneratingCode(false);
    }
  }, [dealInfo]);

  // Copy code to clipboard
  const handleCopyCode = async () => {
    if (!cashbackCode) return;
    try {
      await navigator.clipboard.writeText(cashbackCode);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy code:', error);
    }
  };

  // Calculate cashback discount
  const cashbackDiscount = useMemo(() => {
    if (!cashbackCode || !dealInfo?.cashbackPercent) return 0;
    return (totals.subtotal * dealInfo.cashbackPercent) / 100;
  }, [cashbackCode, dealInfo, totals.subtotal]);

  // Check if minimum purchase is met
  const meetsMinimumPurchase = useMemo(() => {
    if (!dealInfo?.minPurchaseAmount) return true;
    return totals.subtotal >= dealInfo.minPurchaseAmount;
  }, [dealInfo, totals.subtotal]);

  // Get related products based on cart items (same/complementary categories)
  const relatedProducts = useMemo(() => {
    if (!cart || !cart.items || cart.items.length === 0) return [];

    // Get categories and tags from cart items
    const cartCategories = new Set();
    const cartTags = new Set();
    const cartProductIds = new Set();

    cart.items.forEach((item) => {
      const product = getProductById(item.productId);
      if (!product) return;
      cartProductIds.add(product.id);
      if (product.category) {
        cartCategories.add(product.category);
      }
      if (product.tags) {
        product.tags.forEach((tag) => cartTags.add(tag));
      }
    });

    // Find related products not in cart
    return PRODUCTS.filter((product) => {
      if (cartProductIds.has(product.id)) return false;

      // Same category or shared tags
      const sameCategory = cartCategories.has(product.category);
      const sharedTags = product.tags?.some((tag) => cartTags.has(tag));

      return sameCategory || sharedTags;
    }).slice(0, 6);
  }, [cart.items]);

  const handleBack = () => {
    if (step === 'payment') {
      setStep('shipping');
    } else if (step === 'shipping') {
      setShowCheckout(false);
      setShowCart(true);
    }
  };

  const handleShippingSubmit = async (e) => {
    e.preventDefault();

    // Generate cashback code when proceeding to payment (if deal info exists)
    if (dealInfo && !cashbackCode && meetsMinimumPurchase) {
      await generateCashbackCode();
    }

    setStep('payment');
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    // Simulate payment processing
    await new Promise(resolve => setTimeout(resolve, 2000));

    const order = checkout();

    // Add cashback info to order
    const orderWithCashback = {
      ...order,
      cashbackCode: cashbackCode,
      cashbackPercent: dealInfo?.cashbackPercent,
      cashbackEarned: cashbackDiscount,
    };

    setCompletedOrder(orderWithCashback);
    setIsProcessing(false);
    setStep('confirmation');
  };

  const handleClose = () => {
    setShowCheckout(false);
    setStep('shipping');
    setCompletedOrder(null);
    // Reset cashback state
    setCashbackCode(null);
    setCodeError(null);
    setCodeCopied(false);
  };

  if (!showCheckout) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50 animate-fade-in"
        onClick={step !== 'confirmation' ? handleClose : undefined}
      />

      {/* Full Screen Slider */}
      <div className="fixed inset-0 bg-white z-50 animate-slide-in-right overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-white">
          {step !== 'confirmation' && (
            <button
              onClick={handleBack}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
            >
              <ArrowLeft size={20} />
            </button>
          )}
          <div className="flex-1 text-center">
            <h2 className="font-bold text-lg text-gray-900">
              {step === 'shipping' && 'Shipping Details'}
              {step === 'payment' && 'Payment'}
              {step === 'confirmation' && 'Order Confirmed'}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
          >
            <X size={20} />
          </button>
        </div>

        {/* Progress Bar */}
        {step !== 'confirmation' && (
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
            <div className="flex items-center gap-2 max-w-lg mx-auto">
              <div className={`flex-1 h-1 rounded-full ${step === 'shipping' || step === 'payment' ? 'bg-indigo-600' : 'bg-gray-200'}`} />
              <div className={`flex-1 h-1 rounded-full ${step === 'payment' ? 'bg-indigo-600' : 'bg-gray-200'}`} />
            </div>
            <div className="flex justify-between mt-2 text-xs font-medium max-w-lg mx-auto">
              <span className={step === 'shipping' ? 'text-indigo-600' : 'text-gray-400'}>Shipping</span>
              <span className={step === 'payment' ? 'text-indigo-600' : 'text-gray-400'}>Payment</span>
            </div>
          </div>
        )}

        {/* Content */}
        <div className={`flex-1 overflow-y-auto transition-all duration-300 ${orderSummaryExpanded ? 'pb-72' : 'pb-36'}`}>
          {step === 'shipping' && (
            <div className="max-w-lg mx-auto">
              <form onSubmit={handleShippingSubmit} className="p-4 space-y-4">
                {/* Shipping Form */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <input
                      type="text"
                      required
                      value={shippingForm.fullName}
                      onChange={(e) => setShippingForm(prev => ({ ...prev, fullName: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="John Doe"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        required
                        value={shippingForm.email}
                        onChange={(e) => setShippingForm(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="john@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input
                        type="tel"
                        required
                        value={shippingForm.phone}
                        onChange={(e) => setShippingForm(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="+1 234 567 8900"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <input
                      type="text"
                      required
                      value={shippingForm.address}
                      onChange={(e) => setShippingForm(prev => ({ ...prev, address: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="123 Main Street"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                      <input
                        type="text"
                        required
                        value={shippingForm.city}
                        onChange={(e) => setShippingForm(prev => ({ ...prev, city: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="New York"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                      <input
                        type="text"
                        required
                        value={shippingForm.state}
                        onChange={(e) => setShippingForm(prev => ({ ...prev, state: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="NY"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                    <input
                      type="text"
                      required
                      value={shippingForm.zipCode}
                      onChange={(e) => setShippingForm(prev => ({ ...prev, zipCode: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="10001"
                    />
                  </div>
                </div>

                {/* Shipping Options */}
                <div className="pt-4 border-t border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-3">Shipping Method</h3>
                  <div className="space-y-2">
                    <label className="flex items-center gap-3 p-3 border border-indigo-200 bg-indigo-50 rounded-xl cursor-pointer">
                      <input type="radio" name="shipping" defaultChecked className="text-indigo-600" />
                      <Truck size={20} className="text-indigo-600" />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">Standard Shipping</p>
                        <p className="text-xs text-gray-500">5-7 business days</p>
                      </div>
                      <span className={`font-bold ${totals.shipping === 0 ? 'text-emerald-600' : 'text-gray-900'}`}>
                        {totals.shipping === 0 ? 'FREE' : `$${totals.shipping.toFixed(2)}`}
                      </span>
                    </label>
                  </div>
                </div>
              </form>

              {/* Related Products Section */}
              {relatedProducts.length > 0 && (
                <div className="p-4 border-t border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-3">Related Products</h3>
                  <p className="text-xs text-gray-500 mb-3">You might also like these items</p>
                  <div className="grid grid-cols-2 gap-3">
                    {relatedProducts.slice(0, 4).map((product) => (
                      <ProductCard key={product.id} product={product} compact />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 'payment' && (
            <div className="max-w-lg mx-auto">
              <form onSubmit={handlePaymentSubmit} className="p-4 space-y-4">
                {/* Cashback Code Section */}
                {dealInfo && (
                  <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-100">
                    <div className="flex items-center gap-2 mb-3">
                      <Tag size={16} className="text-emerald-600" />
                      <span className="text-sm font-bold text-emerald-700">
                        {dealInfo.cashbackPercent ? `${dealInfo.cashbackPercent}% Cashback` : 'Cashback Deal'}
                      </span>
                    </div>

                    {isGeneratingCode ? (
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Loader2 size={16} className="animate-spin" />
                        <span>Generating your cashback code...</span>
                      </div>
                    ) : codeError ? (
                      <div className="flex items-center gap-2 text-sm text-red-600">
                        <AlertCircle size={16} />
                        <span>{codeError}</span>
                      </div>
                    ) : cashbackCode ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-emerald-200">
                          <span className="font-mono font-bold text-lg text-emerald-700 tracking-wide">
                            {cashbackCode}
                          </span>
                          <button
                            type="button"
                            onClick={handleCopyCode}
                            className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                              codeCopied
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-emerald-600 text-white hover:bg-emerald-700'
                            }`}
                          >
                            {codeCopied ? 'Copied!' : 'Copy'}
                          </button>
                        </div>
                        {cashbackDiscount > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-emerald-600">You'll earn cashback</span>
                            <span className="font-bold text-emerald-700">
                              ${cashbackDiscount.toFixed(2)}
                            </span>
                          </div>
                        )}
                      </div>
                    ) : !meetsMinimumPurchase && dealInfo.minPurchaseAmount ? (
                      <div className="text-sm text-amber-600">
                        Add ${(dealInfo.minPurchaseAmount - totals.subtotal).toFixed(2)} more to qualify for cashback
                      </div>
                    ) : null}
                  </div>
                )}

                {/* Commission Breakdown */}
                {totals.commissionBreakdown.length > 0 && (
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100">
                    <div className="flex items-center gap-2 mb-3">
                      <Store size={16} className="text-indigo-600" />
                      <span className="text-sm font-bold text-indigo-700">Commission Breakdown</span>
                    </div>
                    <div className="space-y-2 text-xs">
                      {/* Seller Revenue - for each merchant selling products */}
                      {totals.commissionBreakdown.map(group => (
                        <div key={group.merchantId} className="flex justify-between">
                          <span className="text-gray-600">{group.merchant?.name} Revenue</span>
                          <span className="font-bold text-gray-900">
                            ${group.sellerAmount.toFixed(2)}
                          </span>
                        </div>
                      ))}

                      {/* Referral Commission - only if referral merchant is different from sellers */}
                      {totals.referralMerchant && totals.commissionBreakdown.some(g => g.referralCommission > 0) && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">{totals.referralMerchant.name} Commission</span>
                          <span className="font-bold text-indigo-600">
                            ${totals.commissionBreakdown.reduce((sum, g) => sum + (g.referralCommission || 0), 0).toFixed(2)}
                          </span>
                        </div>
                      )}

                      {/* Marketplace Commission */}
                      <div className="flex justify-between pt-2 border-t border-indigo-200">
                        <span className="text-gray-600">Marketplace Commission</span>
                        <span className="font-bold text-gray-900">
                          ${totals.commissionBreakdown.reduce((sum, g) => sum + (g.marketplaceCommission || 0), 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Payment Form */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard size={18} className="text-gray-600" />
                    <span className="font-bold text-gray-900">Card Details</span>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Card Number</label>
                    <input
                      type="text"
                      required
                      value={paymentForm.cardNumber}
                      onChange={(e) => setPaymentForm(prev => ({ ...prev, cardNumber: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="4242 4242 4242 4242"
                      maxLength={19}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
                      <input
                        type="text"
                        required
                        value={paymentForm.expiryDate}
                        onChange={(e) => setPaymentForm(prev => ({ ...prev, expiryDate: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="MM/YY"
                        maxLength={5}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">CVV</label>
                      <input
                        type="text"
                        required
                        value={paymentForm.cvv}
                        onChange={(e) => setPaymentForm(prev => ({ ...prev, cvv: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="123"
                        maxLength={4}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name on Card</label>
                    <input
                      type="text"
                      required
                      value={paymentForm.cardName}
                      onChange={(e) => setPaymentForm(prev => ({ ...prev, cardName: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="John Doe"
                    />
                  </div>
                </div>

                {/* Security Note */}
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Lock size={14} />
                  <span>Your payment information is secure and encrypted</span>
                </div>
              </form>

              {/* Related Products Section */}
              {relatedProducts.length > 0 && (
                <div className="p-4 border-t border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-3">Related Products</h3>
                  <p className="text-xs text-gray-500 mb-3">Add more items to your order</p>
                  <div className="grid grid-cols-2 gap-3">
                    {relatedProducts.slice(0, 4).map((product) => (
                      <ProductCard key={product.id} product={product} compact />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 'confirmation' && completedOrder && (
            <div className="p-4 space-y-6 text-center max-w-lg mx-auto">
              {/* Success Icon */}
              <div className="w-20 h-20 mx-auto bg-emerald-100 rounded-full flex items-center justify-center">
                <CheckCircle size={40} className="text-emerald-600" />
              </div>

              <div>
                <h2 className="text-2xl font-black text-gray-900 mb-2">Order Confirmed!</h2>
                <p className="text-gray-500">Thank you for your purchase</p>
              </div>

              {/* Order ID */}
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs text-gray-500 mb-1">Order ID</p>
                <p className="font-mono font-bold text-gray-900">{completedOrder.id}</p>
              </div>

              {/* Cashback Earned */}
              {completedOrder.cashbackCode && completedOrder.cashbackEarned > 0 && (
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Tag size={18} className="text-emerald-600" />
                    <span className="font-bold text-emerald-700">Cashback Earned!</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-emerald-600">Your code: <span className="font-mono font-bold">{completedOrder.cashbackCode}</span></p>
                      <p className="text-xs text-gray-500 mt-1">{completedOrder.cashbackPercent}% cashback on this order</p>
                    </div>
                    <span className="font-black text-2xl text-emerald-600">+${completedOrder.cashbackEarned.toFixed(2)}</span>
                  </div>
                </div>
              )}

              {/* Order Details */}
              <div className="bg-white border border-gray-200 rounded-xl p-4 text-left">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Package size={18} />
                  Order Summary
                </h3>
                <div className="space-y-2 mb-4">
                  {completedOrder.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="text-gray-600">
                        {item.productName} <span className="text-gray-400">x{item.quantity}</span>
                      </span>
                      <span className="text-gray-900">${item.subtotal.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="pt-3 border-t border-gray-100 flex justify-between">
                  <span className="font-bold text-gray-900">Total Paid</span>
                  <span className="font-black text-xl text-emerald-600">${completedOrder.total.toFixed(2)}</span>
                </div>
              </div>

              {/* Commission Summary */}
              {completedOrder.commissionBreakdown?.length > 0 && (
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 text-left border border-indigo-100">
                  <h3 className="font-bold text-indigo-700 mb-3">Commission Breakdown</h3>
                  <div className="space-y-2 text-xs">
                    {/* Seller Revenue */}
                    {completedOrder.commissionBreakdown.map((group, idx) => (
                      <div key={idx} className="flex justify-between">
                        <span className="text-gray-600">{group.merchantName} Revenue</span>
                        <span className="font-bold text-gray-900">
                          ${group.sellerAmount.toFixed(2)}
                        </span>
                      </div>
                    ))}

                    {/* Referral Commission */}
                    {completedOrder.referralMerchantName && completedOrder.commissionBreakdown.some(g => g.referralCommission > 0) && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">{completedOrder.referralMerchantName} Commission</span>
                        <span className="font-bold text-indigo-600">
                          ${completedOrder.commissionBreakdown.reduce((sum, g) => sum + (g.referralCommission || 0), 0).toFixed(2)}
                        </span>
                      </div>
                    )}

                    {/* Marketplace Commission */}
                    <div className="flex justify-between pt-2 border-t border-indigo-200">
                      <span className="text-gray-600">Marketplace Commission</span>
                      <span className="font-bold text-gray-900">
                        ${completedOrder.commissionBreakdown.reduce((sum, g) => sum + (g.marketplaceCommission || 0), 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Continue Shopping Button */}
              <button
                onClick={handleClose}
                className="w-full py-4 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700"
              >
                Continue Shopping
              </button>
            </div>
          )}
        </div>

        {/* Fixed Bottom - Complete Your Order Summary (Expandable) */}
        {step !== 'confirmation' && (
          <div className="fixed bottom-0 left-0 right-0 border-t border-gray-100 bg-white z-10 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
            <div className="max-w-lg mx-auto p-4">
              {/* Expandable Header */}
              <button
                onClick={() => setOrderSummaryExpanded(!orderSummaryExpanded)}
                className="w-full flex items-center justify-between mb-3"
              >
                <h3 className="font-bold text-gray-900">Complete Your Order</h3>
                <div className="flex items-center gap-2">
                  {!orderSummaryExpanded && (
                    <span className="font-black text-xl text-gray-900">${totals.total.toFixed(2)}</span>
                  )}
                  {orderSummaryExpanded ? (
                    <ChevronDown size={20} className="text-gray-400" />
                  ) : (
                    <ChevronUp size={20} className="text-gray-400" />
                  )}
                </div>
              </button>

              {/* Expanded Order Summary Details */}
              <div className={`overflow-hidden transition-all duration-300 ${orderSummaryExpanded ? 'max-h-96 opacity-100 mb-4' : 'max-h-0 opacity-0'}`}>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Subtotal ({totals.itemCount} items)</span>
                    <span className="text-gray-900">${totals.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Shipping</span>
                    <span className={totals.shipping === 0 ? 'text-emerald-600' : 'text-gray-900'}>
                      {totals.shipping === 0 ? 'FREE' : `$${totals.shipping.toFixed(2)}`}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Tax</span>
                    <span className="text-gray-900">${totals.tax.toFixed(2)}</span>
                  </div>
                  {/* Cashback Earned (shown after code generation) */}
                  {cashbackCode && cashbackDiscount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-emerald-600 flex items-center gap-1">
                        <Tag size={12} />
                        Cashback to earn
                      </span>
                      <span className="text-emerald-600 font-medium">+${cashbackDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-gray-100">
                    <span className="font-bold text-gray-900">Total</span>
                    <span className="font-black text-xl text-gray-900">${totals.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              {step === 'shipping' ? (
                <button
                  type="button"
                  onClick={handleShippingSubmit}
                  className="w-full py-4 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 active:scale-[0.98]"
                >
                  Continue to Payment
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handlePaymentSubmit}
                  disabled={isProcessing}
                  className={`w-full py-4 font-bold rounded-xl flex items-center justify-center gap-2 ${
                    isProcessing
                      ? 'bg-indigo-400 cursor-not-allowed'
                      : 'bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98]'
                  } text-white`}
                >
                  {isProcessing ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <Lock size={18} />
                      <span>Pay ${totals.total.toFixed(2)}</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
